import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import ProductPicker from '../components/ProductPicker';

const BRAND_COLORS = {
  'JASMINE': '#6c3fc5', 'LUVEAL': '#e91e8c', 'CAVAYELO': '#0891b2',
  'MAROTA': '#d97706', 'PURE PASSION': '#dc2626', 'JASMINE NICHE': '#7c3aed',
  'VILARA': '#059669', 'VILARA US': '#0ea5e9',
};
const bc = (b) => BRAND_COLORS[b] || '#6d28d9';

export default function FinishedInventory() {
  const { user } = useAuth();
  const [products, setProducts]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterZero, setFilterZero]   = useState(false);
  const [filterSize, setFilterSize]     = useState('');
  const [openBrands, setOpenBrands]   = useState(new Set());

  // مودال التفاصيل
  const [modal, setModal]             = useState(false);
  const [selected, setSelected]       = useState(null);
  const [bom, setBom]                 = useState([]);
  const [bomLoading, setBomLoading]   = useState(false);
  const [stockEdit, setStockEdit]     = useState('');
  const [saving, setSaving]           = useState(false);

  // إضافة مادة للوصفة
  const [addMat, setAddMat]           = useState(false);
  const [newMat, setNewMat]           = useState({ raw_material_id:'', quantity_per_unit:1, flavor_id:'' });
  const [flavors, setFlavors]         = useState([]);
  const [savingMat, setSavingMat]     = useState(false);

  const isAdmin = user?.role === 'admin';
  const isManager = ['admin','sales_manager'].includes(user?.role);

  const load = () => {
    setLoading(true);
    API.get('/finished-products').then(r => { setProducts(r.data); setLoading(false); });
  };

  useEffect(() => {
    load();
    API.get('/flavors').then(r => setFlavors(r.data)).catch(() => {});
  }, []);

  const openModal = async (fp) => {
    setSelected(fp);
    setStockEdit(String(fp.stock_qty || 0));
    setAddMat(false);
    setNewMat({ raw_material_id:'', quantity_per_unit:1, flavor_id:'' });
    setModal(true);
    setBomLoading(true);
    setBom([]);
    try { const r = await API.get(`/bom/${fp.id}`); setBom(r.data); }
    catch(e) { setBom([]); }
    finally { setBomLoading(false); }
  };

  const saveStock = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await API.put(`/finished-products/${selected.id}`, { ...selected, stock_qty: +stockEdit || 0 });
      setSelected(s => ({ ...s, stock_qty: +stockEdit || 0 }));
      setProducts(prev => prev.map(p => p.id === selected.id ? { ...p, stock_qty: +stockEdit || 0 } : p));
    } catch(e) { alert('خطأ في الحفظ'); }
    finally { setSaving(false); }
  };

  const deleteBOMItem = async (id) => {
    if (!window.confirm('حذف هذه المادة من الوصفة؟')) return;
    try {
      await API.delete(`/bom/${id}`);
      setBom(prev => prev.filter(b => b.id !== id));
      setProducts(prev => prev.map(p => p.id === selected.id ? { ...p, bom_count: (p.bom_count||1)-1 } : p));
    } catch(e) { alert('خطأ في الحذف'); }
  };

  const addBOMItem = async () => {
    if (!newMat.raw_material_id || !newMat.quantity_per_unit) return;
    setSavingMat(true);
    try {
      await API.post('/bom', {
        finished_product_id: selected.id,
        raw_material_id: newMat.raw_material_id,
        quantity_per_unit: +newMat.quantity_per_unit,
        flavor_id: newMat.flavor_id || null,
      });
      const r = await API.get(`/bom/${selected.id}`);
      setBom(r.data);
      setProducts(prev => prev.map(p => p.id === selected.id ? { ...p, bom_count: r.data.length } : p));
      setNewMat({ raw_material_id:'', quantity_per_unit:1, flavor_id:'' });
      setAddMat(false);
    } catch(e) { alert(e.response?.data?.error || 'خطأ في الإضافة'); }
    finally { setSavingMat(false); }
  };

  const toggleBrand = (brand) => {
    setOpenBrands(prev => { const n = new Set(prev); n.has(brand) ? n.delete(brand) : n.add(brand); return n; });
  };

  const filtered = products.filter(p =>
    (!search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.product_type?.toLowerCase().includes(search.toLowerCase())) &&
    (!filterBrand || p.brand === filterBrand) &&
    (!filterZero || (p.stock_qty || 0) === 0) &&
    (!filterSize || p.product_type === filterSize)
  );

  const brands = [...new Set(products.map(p => p.brand).filter(Boolean))].sort();
  const sizes = [...new Set(products.map(p => p.product_type).filter(Boolean))].sort();
  const shownBrands = [...new Set(filtered.map(p => p.brand).filter(Boolean))].sort();
  const totalStock = products.reduce((s,p) => s+(p.stock_qty||0), 0);
  const zeroCount = products.filter(p => (p.stock_qty||0) === 0).length;

  const commonBOM = bom.filter(b => !b.flavor_id);
  const flavoredBOM = bom.filter(b => b.flavor_id);
  const bomFlavors = [...new Set(flavoredBOM.map(b => b.flavor_name).filter(Boolean))];

  const printInventory = () => {
    const win = window.open('', '_blank');
    const rows = filtered.map((p,i) => `
      <tr style="background:${i%2===0?'#fff':'#f0f4ff'}">
        <td style="text-align:center;font-weight:800;color:#6d28d9">${i+1}</td>
        <td><span style="background:${bc(p.brand)};color:white;border-radius:4px;padding:1px 7px;font-size:12px;font-weight:700">${p.brand||'-'}</span></td>
        <td style="font-size:12px;color:#475569">${p.product_type||'-'}</td>
        <td style="font-weight:700">${p.name}</td>
        <td style="text-align:center;font-weight:800;color:${(p.stock_qty||0)>0?'#16a34a':'#dc2626'}">${(p.stock_qty||0).toLocaleString()}</td>
        <td style="text-align:center;color:#64748b">${p.bom_count||0}</td>
      </tr>`).join('');
    win.document.write(`<!DOCTYPE html><html lang="ar" dir="rtl"><head><meta charset="utf-8"/><title>المنتجات الجاهزة</title>
      <style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Tahoma,Arial,sans-serif;direction:rtl;padding:24px;font-size:13px}
      h2{font-size:18px;color:#6d28d9;margin-bottom:4px}.meta{font-size:12px;color:#64748b;margin-bottom:16px}
      table{width:100%;border-collapse:collapse}thead tr{background:#1e293b}
      th{color:white;padding:8px 10px;text-align:right;font-size:12px}
      td{padding:7px 10px;border-bottom:1px solid #e2e8f0}
      tr:nth-child(even) td{background:#f0f4ff}
      @media print{@page{margin:1.2cm;size:A4 portrait}body{padding:0;-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style>
      </head><body>
      <h2>🌸 المنتجات الجاهزة</h2>
      <div class="meta">التاريخ: ${new Date().toLocaleDateString('ar-SA')} — ${filtered.length} منتج — مخزون: ${totalStock.toLocaleString()}</div>
      <table><thead><tr><th style="width:36px;text-align:center">#</th><th>الماركة</th><th>النوع</th><th>المنتج</th><th style="text-align:center">المخزون</th><th style="text-align:center">مواد الوصفة</th></tr></thead>
      <tbody>${rows}</tbody></table></body></html>`);
    win.document.close(); setTimeout(() => { win.focus(); win.print(); }, 400);
  };

  const exportCSV = () => {
    const header = ['#','الماركة','النوع','المنتج','المخزون','مواد الوصفة'];
    const rows = filtered.map((p,i) => [i+1, p.brand||'', p.product_type||'', p.name, p.stock_qty||0, p.bom_count||0]);
    const csv = [header,...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF'+csv], {type:'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href=url; a.download=`منتجات_جاهزة_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">🌸 المنتجات الجاهزة</h1>
          <div style={{fontSize:'0.82rem', color:'var(--muted)', marginTop:2}}>
            {products.length} منتج • {brands.length} ماركة • مخزون: <strong style={{color:'var(--success)'}}>{totalStock.toLocaleString()}</strong> قطعة
            {zeroCount > 0 && <span style={{color:'var(--danger)',marginRight:8}}>• ⚠️ {zeroCount} نفد</span>}
          </div>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={printInventory}>🖨️ طباعة</button>
          <button className="btn btn-secondary" onClick={exportCSV}>📊 Excel</button>
        </div>
      </div>

      {/* Filters */}
      <div className="search-bar" style={{flexWrap:'wrap', gap:8, marginBottom:16}}>
        <input className="search-input" placeholder="🔍 بحث باسم المنتج أو النوع..." value={search} onChange={e=>setSearch(e.target.value)} style={{minWidth:200}}/>
        <select value={filterBrand} onChange={e=>setFilterBrand(e.target.value)} style={{minWidth:140}}>
          <option value="">كل الماركات</option>
          {brands.map(b=><option key={b} value={b}>{b}</option>)}
        </select>
        <select value={filterSize} onChange={e=>setFilterSize(e.target.value)} style={{minWidth:160}}>
          <option value="">كل الأنواع/الأحجام</option>
          {sizes.map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        <button className={`btn btn-sm ${filterZero?'btn-danger':'btn-secondary'}`} onClick={()=>setFilterZero(!filterZero)}>⚠️ نفد المخزون</button>
        {(search||filterBrand||filterZero||filterSize) && <button className="btn btn-sm btn-secondary" onClick={()=>{setSearch('');setFilterBrand('');setFilterZero(false);setFilterSize('');}}>✕ مسح</button>}
        <button className="btn btn-sm btn-secondary" onClick={()=>setOpenBrands(new Set(shownBrands))}>▼ فتح الكل</button>
        <button className="btn btn-sm btn-secondary" onClick={()=>setOpenBrands(new Set())}>▲ طي الكل</button>
        <span className="text-muted" style={{fontSize:'0.8rem', marginRight:'auto'}}>{filtered.length} منتج</span>
      </div>

      {loading ? <div className="loading"><div className="spinner"/></div> : (
        <div>
          {shownBrands.map(brand => {
            const brandProds = filtered.filter(p => p.brand === brand);
            const brandStock = brandProds.reduce((s,p) => s+(p.stock_qty||0), 0);
            const isOpen = openBrands.has(brand);
            return (
              <div key={brand} style={{marginBottom:10}}>
                <div onClick={()=>toggleBrand(brand)}
                  style={{display:'flex', alignItems:'center', gap:12, padding:'12px 16px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, cursor:'pointer', transition:'all 0.15s', marginBottom:isOpen?8:0}}
                  onMouseEnter={e=>e.currentTarget.style.borderColor=bc(brand)}
                  onMouseLeave={e=>e.currentTarget.style.borderColor='var(--border)'}>
                  <span style={{background:bc(brand), color:'white', borderRadius:8, padding:'4px 14px', fontWeight:800, fontSize:'0.88rem'}}>{brand}</span>
                  <span className="text-muted" style={{fontSize:'0.8rem'}}>{brandProds.length} منتج</span>
                  <span style={{marginRight:'auto', fontWeight:700, color:'var(--success)', fontSize:'0.9rem'}}>{brandStock.toLocaleString()} قطعة</span>
                  <span style={{color:'var(--muted)', fontSize:'0.75rem', transition:'transform 0.2s', transform:isOpen?'rotate(180deg)':'rotate(0deg)'}}>▼</span>
                </div>
                {isOpen && (
                  <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(240px,1fr))', gap:8}}>
                    {brandProds.map(p => {
                      const stock = p.stock_qty || 0;
                      return (
                        <div key={p.id} onClick={()=>openModal(p)}
                          style={{background:'var(--surface)', border:'1.5px solid var(--border)', borderRadius:10, padding:'12px 14px', cursor:'pointer', transition:'all 0.15s'}}
                          onMouseEnter={e=>{e.currentTarget.style.borderColor=bc(brand); e.currentTarget.style.transform='translateY(-2px)';}}
                          onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.transform='translateY(0)';}}>
                          <div style={{fontWeight:700, fontSize:'0.88rem', marginBottom:4}}>🌸 {p.name}</div>
                          {p.product_type && <div className="text-muted" style={{fontSize:'0.74rem', marginBottom:8}}>{p.product_type}</div>}
                          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                            <span className={`badge ${stock>0?'badge-success':'badge-gray'}`} style={{fontSize:'0.75rem'}}>
                              {stock>0 ? `✅ ${stock.toLocaleString()}` : '⚪ نفد'}
                            </span>
                            <span className="text-muted" style={{fontSize:'0.72rem'}}>{p.bom_count>0?`📋 ${p.bom_count} مادة`:'—'}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && <div className="empty-state"><div className="empty-state-icon">🌸</div><p>لا توجد منتجات</p></div>}
        </div>
      )}

      {/* ===== مودال التفاصيل ===== */}
      {modal && selected && (
        <div className="modal-overlay" onClick={()=>setModal(false)}>
          <div className="modal" style={{maxWidth:680}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div style={{display:'flex', alignItems:'center', gap:8}}>
                <span style={{background:bc(selected.brand), color:'white', borderRadius:6, padding:'2px 10px', fontSize:'0.75rem', fontWeight:700}}>{selected.brand}</span>
                <span className="modal-title" style={{fontSize:'0.95rem'}}>{selected.name}</span>
                {selected.product_type && <span className="text-muted" style={{fontSize:'0.78rem'}}>• {selected.product_type}</span>}
              </div>
              <button className="modal-close" onClick={()=>setModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              {/* تعديل المخزون */}
              {isManager && (
                <div style={{background:'#f0fdf4', border:'1px solid #86efac', borderRadius:10, padding:'12px 16px', marginBottom:16, display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
                  <span style={{fontWeight:700, fontSize:'0.85rem', color:'#15803d'}}>📦 المخزون الجاهز:</span>
                  <input type="number" min={0} value={stockEdit} onChange={e=>setStockEdit(e.target.value)}
                    style={{width:90, padding:'5px 10px', borderRadius:6, border:'1px solid #86efac', fontSize:'0.9rem', textAlign:'center', fontWeight:700}} />
                  <span className="text-muted" style={{fontSize:'0.82rem'}}>قطعة</span>
                  <button className="btn btn-sm btn-primary" onClick={saveStock} disabled={saving}>{saving?'...':'💾 حفظ'}</button>
                  {selected.stock_qty !== +stockEdit && <span style={{fontSize:'0.75rem', color:'var(--warning)'}}>⚠️ غير محفوظ</span>}
                </div>
              )}

              {/* الوصفة */}
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10}}>
                <div style={{fontWeight:700, fontSize:'0.88rem'}}>📋 وصفة الإنتاج</div>
                {isManager && (
                  <button className="btn btn-sm btn-primary" onClick={()=>setAddMat(!addMat)}>
                    {addMat ? '✕ إلغاء' : '+ إضافة مادة'}
                  </button>
                )}
              </div>

              {/* نموذج إضافة مادة */}
              {addMat && (
                <div style={{background:'#f8fafc', border:'1px solid var(--border)', borderRadius:10, padding:'12px 14px', marginBottom:12}}>
                  <div style={{display:'grid', gridTemplateColumns:'2fr 1fr 1fr auto', gap:8, alignItems:'end'}}>
                    <div className="form-group" style={{margin:0}}>
                      <label style={{fontSize:'0.78rem'}}>المادة الأولية</label>
                      <ProductPicker
                        value={newMat.raw_material_id}
                        onChange={id => setNewMat(m => ({...m, raw_material_id: id}))}
                        placeholder="اختر المادة..." />
                    </div>
                    <div className="form-group" style={{margin:0}}>
                      <label style={{fontSize:'0.78rem'}}>الكمية/وحدة</label>
                      <input type="number" min={0.001} step={0.001} value={newMat.quantity_per_unit}
                        onChange={e=>setNewMat(m=>({...m, quantity_per_unit:e.target.value}))}
                        style={{padding:'6px 10px', borderRadius:6, border:'1px solid var(--border)', width:'100%'}} />
                    </div>
                    <div className="form-group" style={{margin:0}}>
                      <label style={{fontSize:'0.78rem'}}>الرائحة (اختياري)</label>
                      <select value={newMat.flavor_id} onChange={e=>setNewMat(m=>({...m, flavor_id:e.target.value}))}
                        style={{padding:'6px 10px', borderRadius:6, border:'1px solid var(--border)', width:'100%', fontSize:'0.85rem'}}>
                        <option value="">مشترك</option>
                        {flavors.map(f=><option key={f.id} value={f.id}>{f.name}</option>)}
                      </select>
                    </div>
                    <button className="btn btn-sm btn-primary" onClick={addBOMItem} disabled={savingMat||!newMat.raw_material_id}
                      style={{marginBottom:0}}>
                      {savingMat?'...':'✅ إضافة'}
                    </button>
                  </div>
                </div>
              )}

              {bomLoading ? <div className="loading"><div className="spinner"/></div> : bom.length === 0 ? (
                <div className="text-muted" style={{textAlign:'center', padding:24}}>⚠️ لا توجد وصفة إنتاج لهذا المنتج</div>
              ) : (
                <>
                  {/* المواد المشتركة */}
                  {commonBOM.length > 0 && (
                    <div style={{marginBottom:14}}>
                      <div style={{fontSize:'0.72rem', fontWeight:700, color:'var(--primary)', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.5px'}}>
                        المواد المشتركة ({commonBOM.length})
                      </div>
                      <div className="table-scroll">
                        <table>
                          <thead><tr><th>المادة</th><th>التصنيف</th><th style={{textAlign:'center'}}>الكمية/وحدة</th><th style={{textAlign:'center'}}>المتوفر</th>{isAdmin&&<th></th>}</tr></thead>
                          <tbody>
                            {commonBOM.map(b=>(
                              <tr key={b.id}>
                                <td className="font-bold" style={{fontSize:'0.85rem'}}>
                                  {b.material_name}
                                  {b.brand&&<span className="text-muted" style={{fontSize:'0.72rem',marginRight:6}}>{b.brand} {b.size}</span>}
                                </td>
                                <td><span className="badge badge-info" style={{fontSize:'0.72rem'}}>{b.category_name||'—'}</span></td>
                                <td style={{textAlign:'center', fontWeight:700}}>{b.quantity_per_unit} {b.material_unit}</td>
                                <td style={{textAlign:'center'}}>
                                  <span className={`badge ${b.current_qty>0?'badge-success':'badge-danger'}`} style={{fontSize:'0.75rem'}}>{(b.current_qty||0).toLocaleString()}</span>
                                </td>
                                {isAdmin&&<td><button className="btn btn-sm btn-danger" onClick={()=>deleteBOMItem(b.id)} style={{padding:'2px 8px'}}>✕</button></td>}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* مواد الروائح */}
                  {bomFlavors.length > 0 && (
                    <div>
                      <div style={{fontSize:'0.72rem', fontWeight:700, color:'#e91e8c', marginBottom:8, textTransform:'uppercase', letterSpacing:'0.5px'}}>
                        مواد خاصة بالروائح ({bomFlavors.length} رائحة)
                      </div>
                      {bomFlavors.map(flavor=>(
                        <div key={flavor} style={{marginBottom:10}}>
                          <div style={{fontSize:'0.78rem', fontWeight:700, marginBottom:5}}>
                            <span style={{background:'var(--primary-light)', color:'var(--primary)', borderRadius:6, padding:'2px 10px'}}>🌸 {flavor}</span>
                          </div>
                          <div className="table-scroll">
                            <table>
                              <tbody>
                                {flavoredBOM.filter(b=>b.flavor_name===flavor).map(b=>(
                                  <tr key={b.id}>
                                    <td className="font-bold" style={{fontSize:'0.85rem'}}>
                                      {b.material_name}
                                      {b.brand&&<span className="text-muted" style={{fontSize:'0.72rem',marginRight:6}}>{b.brand} {b.size}</span>}
                                    </td>
                                    <td><span className="badge badge-info" style={{fontSize:'0.72rem'}}>{b.category_name||'—'}</span></td>
                                    <td style={{textAlign:'center', fontWeight:700}}>{b.quantity_per_unit} {b.material_unit}</td>
                                    <td style={{textAlign:'center'}}>
                                      <span className={`badge ${b.current_qty>0?'badge-success':'badge-danger'}`} style={{fontSize:'0.75rem'}}>{(b.current_qty||0).toLocaleString()}</span>
                                    </td>
                                    {isAdmin&&<td><button className="btn btn-sm btn-danger" onClick={()=>deleteBOMItem(b.id)} style={{padding:'2px 8px'}}>✕</button></td>}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(false)}>إغلاق</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
